import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  HELD_STILL_KEPT, LETTER_MAX_WORDS, MAY_SAY_REPLIED, WRITTEN_TO, lettersGo, wordsIn,
  type ContactView, type ProspectState, type WriteResponse,
} from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";
import { when } from "./Today.js";

/**
 * **One company** — its evidence, its letter, and what happened to it (23 §the screens).
 *
 * A panel rather than a screen, opened from Today's queue or from the Companies list, because a company is not a
 * place in the app: it is a thing you look at and then put down. Two screens used to show halves of it — Write
 * had the sheet and the letter, Follow had the reply and the history — and a person chasing one company had to
 * know which half lived where.
 *
 * Top to bottom is the order somebody reads it in: who they are and what the judge made of them, what was read
 * about them, then the letter with the one sentence written for them marked as such, then send.
 */

export function CompanyPanel({ prospectId, onClose, onNext }: {
  prospectId: string;
  onClose: () => void;
  onNext?: (next: string | null) => void;
}) {
  const q = useQuery({ queryKey: ["customers", "write", prospectId], queryFn: () => api.write(prospectId) });
  const client = useQueryClient();
  const [why, setWhy] = useState(false);
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ["customers", "today"] });
    void client.invalidateQueries({ queryKey: ["customers", "prospects"] });
  };
  const put = (d: WriteResponse) => { client.setQueryData(["customers", "write", prospectId], d); refresh(); };

  const draft = useMutation({ mutationFn: () => api.draftLetter(prospectId), onSuccess: put });
  const edit = useMutation({ mutationFn: (e: { subject?: string; body?: string }) => api.editLetter(q.data!.letter!.id, e), onSuccess: put });
  const discard = useMutation({ mutationFn: () => api.discardLetter(q.data!.letter!.id), onSuccess: put });
  const notAFit = useMutation({
    mutationFn: () => api.notAFit(prospectId, ""),
    onSuccess: (d) => { refresh(); onNext?.(d.queue[0]?.id ?? null); },
  });
  // Where nothing comes next — opened from Companies — the panel itself says the letter went, or Send would change
  // nothing on the screen while the letter left.
  const [sent, setSent] = useState<{ to: string; from: string | null } | null>(null);
  const send = useMutation({
    mutationFn: () => api.sendLetter(q.data!.letter!.id),
    onSuccess: (d) => {
      refresh();
      if (onNext) onNext(d.queue[0]?.id ?? null);
      else setSent({ to: q.data!.letter!.toEmail, from: q.data!.mailbox?.address ?? null });
    },
  });

  /**
   * *They replied*, and then how it went (23 §they replied). A mailbox that can only send never hears an answer, so
   * this is where a person says one came; the three outcomes open after it, on this panel as they do on Today.
   */
  const afterReply = () => { void client.invalidateQueries({ queryKey: ["customers", "write", prospectId] }); refresh(); };
  const replied = useMutation({ mutationFn: () => api.theyReplied(prospectId), onSuccess: afterReply });
  const bounced = useMutation({ mutationFn: () => api.itBounced(prospectId), onSuccess: afterReply });
  const outcome = useMutation({ mutationFn: (o: string) => api.prospectOutcome(prospectId, o), onSuccess: afterReply });

  const d = q.data;
  const company = d?.company;
  const letter = d?.letter;
  // Written to already: a second first letter is never offered, and what can happen next is an answer.
  const written = Boolean(company && (WRITTEN_TO.includes(company.prospect.state) || d?.lastSent));
  // The law of the company's country, asked here as the API asks it, so the press is not offered at all where it
  // would only be refused (ADR 0065). The company stays: the judge, the sheet and the history are all still here.
  const law = company ? lettersGo(company.prospect.country) : null;
  const held = law && !law.goes ? law.says : null;

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-label="One company">
      <button className="flex-1 bg-ink/20" onClick={onClose} aria-label="Close" />
      <div className="w-full max-w-[730px] bg-panel border-l border-line shadow-2xl flex flex-col overflow-y-auto">
        {q.isPending || !company ? (
          <p className="p-6 text-sm text-faint">…</p>
        ) : (
          <div className="p-5 flex flex-col gap-3.5">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-base font-semibold">{company.prospect.companyName}</h1>
              {company.prospect.verdict && (
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-page border border-line-2 text-muted">
                  {company.prospect.verdict} · {company.prospect.score}
                </span>
              )}
              <button onClick={() => setWhy(!why)} className="text-[11.5px] text-coral underline underline-offset-2">why?</button>
              <span className="text-[11.5px] text-faint ml-auto">
                {company.prospect.domain ?? "no website"} · {company.prospect.country}
              </span>
              <button onClick={onClose} className="p-1 rounded hover:bg-page" aria-label="Close"><Icon name="x" size={14} /></button>
            </div>

            {company.prospect.contact && <Contact contact={company.prospect.contact} />}

            {why && (
              <div className="rounded-lg border border-line bg-page p-3 flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">The judge</span>
                <span className="text-[12px] text-muted leading-relaxed">
                  Scored {company.prospect.score ?? 0} out of {company.outOf}
                  {company.judgeVersion ? ` · by version ${company.judgeVersion} of your table` : ""}.
                </span>
                {company.missing.length > 0 && (
                  <span className="text-[12px] text-muted leading-relaxed">
                    <b>Looked for and not found:</b> {company.missing.map((m) => m.fact).join(" · ")}
                  </span>
                )}
              </div>
            )}

            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">What we know</span>
              {/* Keyed by place in the sheet too: the lines about a company (`about`) read on one page share their fact
                  and their page, and a key they share kept the last company's lines on this one (live, 2026-10-21). */}
              {company.facts.map((f, i) => (
                <div key={`${company.prospect.id}:${i}:${f.id}:${f.url}`} className="flex gap-2 items-start py-1 border-b border-line last:border-0">
                  <Icon name={f.refusal ? "x" : "check"} size={12} className={`mt-1 ${f.refusal ? "text-bad" : "text-good"}`} />
                  <span className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <span className="text-[12.5px] leading-snug">{f.says}</span>
                    <span className="text-xs text-faint truncate">{f.url}</span>
                  </span>
                  {f.points !== null && f.points !== 0 && (
                    <span className={`text-[11px] font-semibold tabular-nums shrink-0 ${f.points > 0 ? "text-good" : "text-bad"}`}>
                      {f.points > 0 ? "+" : ""}{f.points}
                    </span>
                  )}
                </div>
              ))}
              {!company.facts.length && <span className="text-xs text-muted">Nothing has been read about it yet.</span>}
            </div>

            {sent
              ? (
                <div className="rounded-xl border border-line bg-page p-4 flex items-start gap-2" role="status">
                  <Icon name="check" size={14} className="text-good mt-0.5" />
                  <p className="text-[12.5px] leading-relaxed">
                    The letter to <b>{sent.to}</b> is on its way. It leaves{sent.from ? <> from {sent.from}</> : null} within
                    a minute, and lands in that mailbox's Sent folder.
                  </p>
                </div>
              )
              : letter
              ? <Letter d={d!} onEdit={(e) => edit.mutate(e)} onSend={() => send.mutate()} onDiscard={() => discard.mutate()} sending={send.isPending} error={send.error} next={!!onNext} held={held} />
              : written
              ? (
                <WrittenTo
                  state={company.prospect.state} sent={d!.lastSent}
                  onReplied={() => replied.mutate()} replying={replied.isPending || bounced.isPending}
                  onBounced={() => bounced.mutate()}
                  onOutcome={(o) => outcome.mutate(o)} deciding={outcome.isPending}
                  error={replied.error ?? bounced.error ?? outcome.error}
                />
              )
              : held
              ? (
                <div className="rounded-xl border border-line bg-page p-4" role="note">
                  <p className="text-[12.5px] text-muted leading-relaxed">No letter: {held} {HELD_STILL_KEPT}</p>
                </div>
              )
              : (
                <div className="rounded-xl border border-line bg-page p-4 flex flex-col gap-2">
                  {company.prospect.email ? (
                    <>
                      <p className="text-[12.5px] text-muted leading-relaxed">No letter yet. The opening line is written from the sheet above, and nothing else.</p>
                      <button
                        onClick={() => draft.mutate()} disabled={draft.isPending}
                        className="self-start px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-50"
                      >{draft.isPending ? "Writing…" : "Write the letter · about 1¢"}</button>
                      {draft.error ? <p className="text-xs text-bad">{String(draft.error)}</p> : null}
                    </>
                  ) : (
                    <p className="text-[12.5px] text-muted leading-relaxed">
                      No address was found on their own site, so there is nobody to write to. A guessed address
                      bounces, and bounces close a sending account.
                    </p>
                  )}
                </div>
              )}

            <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-muted">
              {!written && !held && (
                <span>
                  {d.mailbox && !d.mailbox.followsUp
                    ? "One letter, no follow-ups: an answer lands in your own inbox."
                    : "Then: a follow-up on day 4, another on day 11, and a reply stops both."}
                </span>
              )}
              <button
                onClick={() => notAFit.mutate()} disabled={notAFit.isPending}
                className="ml-auto underline underline-offset-2 hover:text-ink disabled:opacity-50"
              >Not a fit</button>
            </div>

            {company.history.length > 0 && (
              <details className="text-[12px]">
                <summary className="cursor-pointer text-muted">Everything that has happened</summary>
                <div className="flex flex-col gap-1 pt-2">
                  {company.history.map((h, i) => (
                    <div key={i} className="flex gap-2 text-[11.5px]">
                      <span className="text-faint shrink-0 w-[92px]">{when(h.at)}</span>
                      <span className="text-muted">{h.note ?? `${h.fromState ?? "?"} → ${h.toState ?? "?"}`}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The person a list brought: who reads the letter, and what the list said of them and of their company. Shown so
 * whoever presses Send knows whose inbox it lands in — and what the footer will say about where the address came from.
 */
function Contact({ contact }: { contact: ContactView }) {
  const said = [contact.employees ? `${contact.employees} people` : null, contact.industry, contact.city].filter(Boolean);
  return (
    <div className="rounded-lg border border-line bg-page px-3 py-2 flex flex-col gap-0.5">
      <span className="text-[12.5px]">
        To <b>{[contact.firstName, contact.lastName].filter(Boolean).join(" ") || "a named person"}</b>
        {contact.role ? <span className="text-muted">, {contact.role}</span> : null}
      </span>
      <span className="text-[11px] text-faint">
        from a list · listed in {contact.listedIn}{contact.emailStatus ? ` (${contact.emailStatus})` : ""}
        {said.length ? ` · ${said.join(" · ")}` : ""}
      </span>
    </div>
  );
}

/** What the three outcomes are called on the buttons, the same words Today uses. */
const OUTCOMES = [["meeting", "A call is booked"], ["later", "Not now"], ["declined", "Said no"]] as const;

/**
 * A company already written to: when and where, and what a person can say next.
 *
 * *They replied* while nothing has been heard, because a mailbox that can only send would otherwise never know; then
 * the three outcomes, because what an answer led to is the number the whole loop is for. Any later state just says
 * where it ended, in the list's own words.
 */
function WrittenTo({ state, sent, onReplied, onBounced, replying, onOutcome, deciding, error }: {
  state: ProspectState;
  sent: WriteResponse["lastSent"];
  onReplied: () => void;
  onBounced: () => void;
  replying: boolean;
  onOutcome: (o: string) => void;
  deciding: boolean;
  error: unknown;
}) {
  return (
    <div className="rounded-xl border border-line bg-page p-4 flex flex-col gap-2.5">
      <p className="text-[12.5px] leading-relaxed">
        {sent ? <>Written to <b>{sent.toEmail}</b> {when(sent.sentAt)}, under “{sent.subject}”.</> : "Already written to."}
      </p>
      {MAY_SAY_REPLIED.includes(state) && (
        <div className="flex flex-col gap-1.5">
          <p className="text-[12px] text-muted leading-relaxed">
            Did they answer, or did your inbox say it could not be delivered? Say so, and anything still to go to them stops.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onReplied} disabled={replying}
              className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-50"
            >{replying ? "…" : "They replied"}</button>
            <button
              onClick={onBounced} disabled={replying}
              className="px-3 py-1.5 rounded-lg border border-line text-xs font-semibold disabled:opacity-50"
            >It bounced</button>
          </div>
        </div>
      )}
      {state === "replied" && (
        <div className="flex flex-col gap-1.5">
          <p className="text-[12px] text-muted leading-relaxed">They replied. How did it go?</p>
          <div className="flex flex-wrap gap-1.5">
            {OUTCOMES.map(([o, label], i) => (
              <button
                key={o} onClick={() => onOutcome(o)} disabled={deciding}
                className={`px-2.5 py-1 rounded-lg text-[11.5px] disabled:opacity-50 ${i === 0 ? "bg-ink text-white font-semibold" : "border border-line-2 bg-panel font-medium"}`}
              >{label}</button>
            ))}
          </div>
        </div>
      )}
      {error ? <p className="text-xs text-bad">{String(error)}</p> : null}
    </div>
  );
}

/** The letter as it will be sent: the frame, the one written sentence, the footer nobody edits, and the guard. */
function Letter({ d, onEdit, onSend, onDiscard, sending, error, next, held }: {
  d: WriteResponse;
  onEdit: (v: { subject?: string; body?: string }) => void;
  onSend: () => void;
  onDiscard: () => void;
  sending: boolean;
  error: unknown;
  /** Whether another company opens after this one; "and next" is only said where it is true. */
  next: boolean;
  /** Why no letter may go to this company's country, for a draft written before it was held (ADR 0065). */
  held: string | null;
}) {
  const letter = d.letter!;
  const [subject, setSubject] = useState(letter.subject);
  const [body, setBody] = useState(letter.body);
  const dirty = subject !== letter.subject || body !== letter.body;
  const words = wordsIn(body);
  const blocked = d.guard.length > 0 || held !== null;

  return (
    <div className="rounded-xl border border-line bg-page overflow-hidden flex flex-col">
      <div className="px-3.5 py-2 border-b border-line flex flex-wrap items-center gap-2">
        <span className="text-[11.5px] text-muted">
          To <b className="text-ink">{letter.toEmail}</b>{d.mailbox ? <> from {d.mailbox.address}</> : null}
        </span>
        {letter.openerBy === "model" && <span className="ml-auto text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-panel border border-line-2 text-muted">a model wrote the opening line</span>}
      </div>

      <div className="p-3.5 flex flex-col gap-2.5">
        <input
          value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Subject"
          className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]"
        />
        <textarea
          value={body} onChange={(e) => setBody(e.target.value)} rows={9} aria-label="The letter"
          className="px-2.5 py-2 rounded-lg border border-line-2 bg-panel text-[12.5px] leading-relaxed resize-y"
        />
        {/* What a stranger is being asked to read. A letter is short or it is not read, and the number is the
            only way anybody notices their offer has grown into a page. */}
        <span className={`text-[10.5px] self-end ${words > LETTER_MAX_WORDS ? "text-bad" : "text-faint"}`}>
          {words} words{words > LETTER_MAX_WORDS ? ` · ${LETTER_MAX_WORDS} is the most that will go` : ""}
        </span>
        <p className="text-[10.5px] text-faint leading-relaxed border-t border-line pt-2">
          {letter.closing} <span className="text-faint">· the footer, which nobody edits</span>
        </p>
      </div>

      {held && (
        <div className="px-3.5 py-2 border-t border-line bg-accent/5 flex flex-col gap-1">
          <span className="text-[12px] font-semibold text-accent">This will not go</span>
          <span className="text-[11.5px] text-muted">{held} {HELD_STILL_KEPT}</span>
        </div>
      )}
      {d.guard.length > 0 && (
        <div className="px-3.5 py-2 border-t border-line bg-accent/5 flex flex-col gap-1">
          <span className="text-[12px] font-semibold text-accent">This will not go as it is</span>
          {d.guard.map((g) => <span key={g.rule} className="text-[11.5px] text-muted">{g.says} — <b>{g.found}</b></span>)}
        </div>
      )}

      <div className="px-3.5 py-2.5 border-t border-line flex flex-wrap items-center gap-2">
        {!blocked && !dirty && (
          <span className="text-[11.5px] text-muted flex items-center gap-1.5">
            <Icon name="check" size={12} className="text-good" />The guard read it: nothing your page does not back
          </span>
        )}
        {dirty && <span className="text-[11.5px] text-muted">Save your changes and the guard will read them before anything goes.</span>}
        <div className="ml-auto flex gap-1.5">
          {dirty
            ? <button onClick={() => onEdit({ subject, body })} className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold">Save</button>
            : (
              <button
                onClick={onSend} disabled={blocked || sending}
                className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40"
              ><Icon name="send" size={13} /> {sending ? "Sending…" : next ? "Send · and next" : "Send"}</button>
            )}
          <button onClick={onDiscard} className="px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium">Throw it away</button>
        </div>
      </div>
      {error ? <p className="px-3.5 pb-2 text-xs text-bad">{String(error)}</p> : null}
    </div>
  );
}
